import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import LoadingScreen from "../components/LoadingScreen";
import SplashCursor from "../components/SplashCursor";

export default function TestingPage() {
  const [isLoading, setIsLoading] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("skipIntro") !== "1";
  });
  const layers = useMemo(
    () => [
      {
        name: "Starry Night",
        front: { base: [0.01, 0.012, 0.035], tint: [0.015, 0.02, 0.06] },
        seed: 0.12,
        kind: 1,
        text: { enabled: true, color: [1, 0.55, 0.26] },
      },
      {
        name: "Sunrise",
        front: { base: [0.18, 0.06, 0.3], tint: [0.99, 0.55, 0.22] },
        seed: 1.3,
        kind: 2,
        text: { enabled: true, color: [0.35, 0.72, 1.0] },
      },
      {
        name: "Beta Sky",
        front: { base: [0.28, 0.55, 0.92], tint: [0.78, 0.9, 0.98] },
        seed: 2.05,
        kind: 4,
        text: { enabled: true, color: [0.06, 0.08, 0.14] },
      },
      {
        name: "Storm",
        front: { base: [0.18, 0.2, 0.26], tint: [0.55, 0.6, 0.68] },
        seed: 2.75,
        kind: 5,
        text: { enabled: true, color: [0.9, 0.95, 1.0] },
      },
      {
        name: "Rainbow",
        front: { base: [0.98, 0.9, 0.84], tint: [0.95, 0.78, 0.82] },
        seed: 6.1,
        kind: 6,
        text: { enabled: true, color: [0.92, 0.25, 0.3] },
      },
      {
        name: "Snow",
        front: { base: [0.02, 0.03, 0.08], tint: [0.05, 0.07, 0.13] },
        seed: 5.6,
        kind: 3,
        text: { enabled: true, color: [1.0, 0.9, 0.2] },
      },
    ],
    [],
  );
  const [layerIndex, setLayerIndex] = useState(() => {
    const layerParam = new URLSearchParams(window.location.search).get("layer");
    const parsed = Number.parseInt(layerParam ?? "", 10);
    if (!Number.isFinite(parsed)) return 0;
    const normalized =
      ((parsed % layers.length) + layers.length) % layers.length;
    return normalized;
  });
  const [transitionTarget, setTransitionTarget] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const transitionTimeoutRef = useRef(null);
  const splashRef = useRef(null);
  const transitionDuration = 950;
  const currentLayer = layers[layerIndex];
  const nextIndex = (layerIndex + 1) % layers.length;
  const revealLayerIndex =
    transitionTarget != null ? transitionTarget : nextIndex;
  const revealLayer = layers[revealLayerIndex];

  useEffect(() => {
    return () => {
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
      }
    };
  }, []);

  // Reset fluid once after the layer swap settles (extra safety net).
  useEffect(() => {
    if (transitionTarget != null) return;
    // Double-reset: immediate + next frame to catch any sim artifacts.
    splashRef.current?.resetFluid?.();
    const frame = window.requestAnimationFrame(() => {
      splashRef.current?.resetFluid?.();
    });
    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [layerIndex, transitionTarget]);

  const handleCycle = useCallback(
    (event) => {
      if (isLoading) return;
      if (isTransitioning) return;
      const target = event?.target;
      if (target?.closest?.("a, button, input, textarea, select, label"))
        return;
      if (target?.closest?.("[role='button'], [role='link']")) return;
      if (target?.closest?.("[data-theme-cycle-ignore]")) return;
      const nextIndex = (layerIndex + 1) % layers.length;
      splashRef.current?.resetFluid?.();
      setTransitionTarget(nextIndex);
      setIsTransitioning(true);
      splashRef.current?.triggerTransition({
        x: event?.clientX,
        y: event?.clientY,
        duration: transitionDuration,
      });
      transitionTimeoutRef.current = setTimeout(() => {
        // End the transition circle and clear fluid atomically BEFORE
        // swapping layers so no stale dye from the previous reveal leaks
        // through as a trail on the new layer.
        splashRef.current?.endTransition();
        splashRef.current?.resetFluid?.();
        setLayerIndex(nextIndex);
        setTransitionTarget(null);
        setIsTransitioning(false);
      }, transitionDuration);
    },
    [isLoading, isTransitioning, layerIndex, layers.length],
  );

  const isStarryLayer = layerIndex === 0;
  const surfaceBandStrength = isStarryLayer ? 0 : 0.25;
  const surfaceGrainStrength = isStarryLayer ? 0 : 0.02;

  return (
    <div
      className={`testing-page testing-page--splash${
        isStarryLayer ? " testing-page--starry-layer" : ""
      }`}
      onClick={handleCycle}
    >
      {isLoading ? (
        <LoadingScreen onComplete={() => setIsLoading(false)} />
      ) : null}
      <Link to="/" className="testing-page__home-link app-header__logo">
        DYLAN CHEN ZHEN
      </Link>
      <div className="testing-page__top-right" data-theme-cycle-ignore>
        <div className="testing-page__top-links">
          <span
            className="app-header__nav app-header__nav--disabled"
            aria-disabled="true"
          >
            Projects
          </span>
          <span
            className="app-header__nav app-header__nav--disabled"
            aria-disabled="true"
          >
            About
          </span>
        </div>
        <p className="testing-page__revamp">
          everything is kinda under revamp... using webgl framework broke the
          website
        </p>
      </div>
      <SplashCursor
        ref={splashRef}
        MAX_DEVICE_PIXEL_RATIO={1}
        CURL={0}
        SPLAT_FORCE={2600}
        DENSITY_DISSIPATION={4.6}
        VELOCITY_DISSIPATION={4.2}
        PRESSURE_ITERATIONS={26}
        SPLAT_RADIUS={0.16}
        frontPalette={currentLayer.front}
        backPalette={revealLayer.front}
        frontSeed={currentLayer.seed}
        backSeed={revealLayer.seed}
        frontKind={currentLayer.kind}
        backKind={revealLayer.kind}
        frontTextEnabled={currentLayer.text.enabled}
        backTextEnabled={revealLayer.text.enabled}
        frontTextColor={currentLayer.text.color}
        backTextColor={revealLayer.text.color}
        surfaceBandStrength={surfaceBandStrength}
        surfaceGrainStrength={surfaceGrainStrength}
      />
    </div>
  );
}
